import { useEffect, useRef, useState } from 'react';
import { io, ManagerOptions, Socket, SocketOptions } from 'socket.io-client';

import { useApi } from '@/shared/api';
import { getBearerToken } from '@/shared/api/utils';
import { AccessTokenPayload, extractTokenPayload } from '@/shared/helpers/auth';
import { useAppLogout } from '@/shared/hooks/useAppLogout';
import { useAuthStore } from '@/shared/store/auth.store';
import { log } from '@/shared/utils/log';
import { withResolvers } from '@/shared/utils/promises';

// Shared lock to prevent multiple sockets from refreshing the token simultaneously
let refreshPromise: ReturnType<typeof withResolvers<string | null>> | null = null;

type UseSocketConnectionProps = {
  url: string;
  namespace: string;
  options?: Partial<ManagerOptions & SocketOptions>;
};

export const useSocketConnection = ({ url, namespace, options }: UseSocketConnectionProps) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { accessToken, setAccessToken } = useAuthStore();
  const { appLogout } = useAppLogout();
  const api = useApi();

  useEffect(() => {
    const token = accessToken;

    if (!token && socket) {
      log.trace(`[WebSocket] [${namespace}] Token cleared, disconnecting from the service...`);
      socket.disconnect();
      setSocket(null);
    }

    if (!token) return undefined;

    log.trace(`[WebSocket] [${namespace}] Connecting to the service...`);
    const newSocket = io(url, {
      extraHeaders: {
        Authorization: getBearerToken(token)!,
      },
      reconnection: false,
      ...options,
    });

    setSocket(newSocket);

    const scheduleReconnect = () => {
      log.trace(`[WebSocket] [${namespace}] Reconnect attempt scheduled`);
      reconnectTimeoutRef.current = setTimeout(() => {
        if (newSocket.disconnected) {
          newSocket.connect();
        }
      }, 10000);
    };

    newSocket.on('connect', () => {
      log.trace(`[WebSocket] [${namespace}] Connected to the service`);
    });

    newSocket.on('disconnect', async (reason: string) => {
      log.trace(`[WebSocket] [${namespace}] Disconnected:`, reason);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }

      // Don't reconnect if socket.disconnect() was called
      if (reason === 'io client disconnect') {
        return;
      }

      const currentAccessToken = useAuthStore.getState().accessToken;

      if (!currentAccessToken) {
        log.trace(`[WebSocket] [${namespace}] No auth tokens available`);
        await appLogout();
        return;
      }

      if (!navigator.onLine) {
        log.trace(`[WebSocket] [${namespace}] No internet connection`);
        scheduleReconnect();
        return;
      }

      const jwtData = extractTokenPayload<AccessTokenPayload>(currentAccessToken);
      let validToken: string | null = currentAccessToken;

      if (!jwtData.exp || jwtData.exp < Date.now() / 1000) {
        log.trace(`[WebSocket] [${namespace}] Token expired, refreshing...`);

        if (refreshPromise) {
          validToken = await refreshPromise.promise;
        } else {
          refreshPromise = withResolvers<string | null>();

          const { data: authData } = await api.refreshAuthToken();

          // check the second time after request
          if (!navigator.onLine) {
            scheduleReconnect();
            refreshPromise.resolve(null);
            return;
          }
          if (!authData) {
            log.error(`[WebSocket] [${namespace}] Token refresh failed`);
            await appLogout();
            refreshPromise.resolve(null);
            return;
          }
          setAccessToken(authData.accessToken);

          refreshPromise.resolve(authData.accessToken);

          validToken = await refreshPromise.promise;
          refreshPromise = null;
        }
      }

      if (!validToken) {
        log.trace(`[WebSocket] [${namespace}] No valid token`);
        await appLogout();
        return;
      }

      newSocket.io.opts.extraHeaders = {
        Authorization: getBearerToken(validToken)!,
      };

      scheduleReconnect();
    });

    newSocket.on('connect_error', (error: Error) => {
      log.error(`[WebSocket] [${namespace}] Connection error:`, error);
    });

    newSocket.on('error', (error: Error) => {
      log.error(`[WebSocket] [${namespace}] Socket error:`, error);
    });

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }

      log.trace(`[WebSocket] [${namespace}] Disconnecting...`);
      newSocket.disconnect();
      setSocket(null);
    };
  }, [accessToken]);

  return socket;
};
